import {
  XStack,
  SizableText,
  YStack,
  Paragraph,
  Stack,
  styled,
  GetProps,
  useMedia,
  interaction,
  isWeb,
} from '@mythical/ui'
import useRequester from 'app/hooks/use-requester'
import { likeResult, toggleLike } from 'app/mutations/comments/toggle-like'
import {
  Comment as CommentType,
  createCommentQuery,
  mapToComment,
} from 'app/queries/comments/comments'
import { useStores } from 'app/store'
import { formatTimeAgo } from 'app/lib/format-time-ago'
import { observer } from 'mobx-react-lite'
import { useEffect, useState, useRef } from 'react'

import { Avatar } from '../primitive/Avatar'
import { IconChatCircle, IconHeart } from '../primitive/Icon'
import { IconHeartFull } from '../primitive/Icon/icons/IconHeartFull'
import { Menu, MenuItem } from '../primitive/Menu'
import { CommentForm } from './CommentForm'
import { CommentAction, HandleCommentAction } from './CommentsList'

const CommentWrapper = styled(XStack, {
  justifyContent: 'flex-start',
  bg: '$bgSecondary',
  borderRadius: '$3',
  variants: {
    isReply: {
      true: {
        py: '$0',
        px: '$0',
        pl: '$2',
        $gtXs: {
          px: '$0',
          py: '$0',
          pl: '$0',
        },
      },
      false: {
        py: '$3',
        px: '$2',
        $gtXs: {
          px: '$4',
          py: '$4',
        },
      },
    },
    highlighted: {
      true: {
        bg: '$red10',
      },
    },
  } as const,
  defaultVariants: {
    isReply: false,
  },
})

type CommentWrapperProps = GetProps<typeof CommentWrapper>

interface CommentProps extends CommentWrapperProps {
  item: CommentType
  level: number
  handleAction: HandleCommentAction
  targetCommentId?: string | null
  onUpdate?: (all?: boolean) => Promise<void>
  onCommentLayout?: (commentId: string, ref: any) => void
}

export const Comment = observer(
  ({
    item: initial,
    level,
    onUpdate,
    handleAction,
    targetCommentId,
    onCommentLayout,
    ...props
  }: CommentProps) => {
    const [likeLoading, setLikeLoading] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [item, setItem] = useState(initial)
    const [editing, setEditing] = useState(false)
    const [replying, setReplying] = useState(false)
    const commentRef = useRef<any>(null)

    useEffect(() => {
      setItem(initial)
    }, [initial])

    const media = useMedia()

    const { userStore } = useStores()
    const { request } = useRequester()

    const refreshComment = async () => {
      const query = createCommentQuery()

      try {
        const result = await request(query, { id: initial.id }, mapToComment)
        if (result && result.id) {
          setItem(result)
        }
      } catch (e) {
        console.error(e)
      }
    }

    const handleLike = async () => {
      if (likeLoading) return

      const originalItem = item
      setLikeLoading(true)
      setItem((prev) => ({
        ...prev,
        hasLiked: !prev.hasLiked,
        likes: prev.hasLiked ? prev.likes - 1 : prev.likes + 1,
      }))

      try {
        const result = await request(toggleLike(), { id: item.id }, likeResult)
        if (result?.success) {
          await refreshComment()
        } else {
          setItem(originalItem)
        }
      } catch (error) {
        console.error('Failed to toggle like:', error)
        setItem(originalItem)
      } finally {
        setLikeLoading(false)
      }
    }

    const handleMenuAction = (action: CommentAction) => {
      setMenuOpen(false)
      handleAction(action, item)
    }

    const isOwner = userStore?.user?.id === item.memberId

    useEffect(() => {
      if (targetCommentId === item.id && commentRef.current && onCommentLayout) {
        onCommentLayout(item.id, commentRef.current)
      }
    }, [targetCommentId, item.id, onCommentLayout])

    return (
      <CommentWrapper
        ref={commentRef}
        id={`comment-${item.id}`}
        key={item.id}
        highlighted={targetCommentId === item.id}
        {...props}
      >
        <Avatar
          src={item.profileImage}
          width={40}
          height={40}
          mr="$2"
          {...(media.xs && { width: 20, height: 20, top: 4, mr: '$1' })}
          {...(isOwner && { boc: '$fgBrand', bw: 2 })}
        />

        <YStack fg={1} fs={1} gap="$3.5" pl="$2" $gtSm={{ gap: '$4', pl: 0 }}>
          <YStack fg={1} fs={1} gap="$2">
            <XStack fg={1} fs={1} gap="$3.5" ai="center">
              <XStack fg={1} fs={1} gap="$2" ai="center">
                <SizableText size="$3" fow="500" numberOfLines={1} fs={1}>
                  {item.memberName}
                </SizableText>
                <SizableText size="$2" whiteSpace="nowrap" color="$fgSecondary">
                  {formatTimeAgo(item.createdAt)}
                </SizableText>
                {item.hasEdited && (
                  <SizableText size="$2" fontStyle="italic">
                    (Edited)
                  </SizableText>
                )}
              </XStack>

              <Stack fs={0} ml="auto">
                {item.memberId && (
                  <Menu
                    open={menuOpen}
                    onOpenChange={(open) => setMenuOpen(open)}
                  >
                    {isOwner && (
                      <MenuItem
                        onPress={() => {
                          setEditing(!editing)
                          setMenuOpen(false)
                        }}
                      >
                        {editing ? 'Cancel' : 'Edit'}
                      </MenuItem>
                    )}
                    {isOwner && (
                      <MenuItem onPress={() => handleMenuAction('delete')}>
                        Delete
                      </MenuItem>
                    )}
                    {!isOwner && (
                      <MenuItem onPress={() => handleMenuAction('report')}>
                        Report
                      </MenuItem>
                    )}
                    {!isOwner && (
                      <MenuItem onPress={() => handleMenuAction('block')}>
                        Block
                      </MenuItem>
                    )}
                  </Menu>
                )}
              </Stack>
            </XStack>

            <YStack gap="$3" flexShrink={1}>
              {!editing && (
                <Paragraph
                  size="$2"
                  lineHeight="$3"
                  fontStyle={item.content ? 'normal' : 'italic'}
                  whiteSpace="pre-wrap"
                  userSelect="all"
                >
                  {item.content || 'Deleted Comment'}
                </Paragraph>
              )}
              {editing && (
                <CommentForm
                  px="$0"
                  $gtXs={{ px: '$0' }}
                  id={item.id}
                  comment={item.content}
                  isEditing
                  onCancel={() => setEditing(false)}
                  onSubmitted={async () => {
                    await onUpdate?.()
                    setEditing(false)
                  }}
                />
              )}

              {!editing && !item.deleted && (
                <XStack ai="center" gap="$3">
                  <XStack
                    tag="button"
                    ai="center"
                    gap="$1.5"
                    cursor="pointer"
                    onPress={handleLike}
                    {...interaction.opacity}
                    {...(item.hasLiked && { focusStyle: { opacity: 1 } })}
                  >
                    {item.hasLiked ? (
                      <IconHeartFull size={18} />
                    ) : (
                      <IconHeart size={18} />
                    )}

                    <SizableText
                      color="$fgSecondary"
                      size="$2"
                    >{`${item.likes}`}</SizableText>
                  </XStack>
                  {level < 3 && (
                    <XStack
                      tag="button"
                      ai="center"
                      gap="$1.5"
                      cursor="pointer"
                      onPress={() => setReplying(!replying)}
                      {...interaction.opacity}
                    >
                      <IconChatCircle size={18} />
                      <SizableText color="$fgSecondary" size="$2">
                        Reply
                      </SizableText>
                    </XStack>
                  )}
                </XStack>
              )}
            </YStack>
          </YStack>

          {replying && (
            <CommentForm
              p="$0"
              $gtXs={{ p: '$0' }}
              isReply
              parent={item.id}
              onSubmitted={async () => {
                await onUpdate?.()
                setReplying(false)
              }}
            />
          )}

          {item.replies && item.replies.length > 0 && (
            <YStack fs={1} fg={1} gap="$2">
              {item.replies.map((reply: CommentType) => (
                <Comment
                  isReply
                  item={reply}
                  key={reply.id}
                  level={level + 1}
                  onUpdate={onUpdate}
                  handleAction={handleAction}
                  targetCommentId={targetCommentId}
                  onCommentLayout={onCommentLayout}
                />
              ))}
            </YStack>
          )}
        </YStack>
      </CommentWrapper>
    )
  },
)
